import {useEffect, useState} from 'react';
import { createRoot } from 'react-dom/client';
import { Amplify } from 'aws-amplify';
import {
  Alert,
  Button,
  Loader,
  Message,
  View,
  Link
} from '@aws-amplify/ui-react';
import { fetchUserAttributes, signInWithRedirect, signOut, getCurrentUser } from 'aws-amplify/auth';
import { RedocStandalone, SideNavStyleEnum } from 'redoc';
import '@aws-amplify/ui-react/styles.css';
import './index.css'

const cognitoClientId = '18vtuf9nm8aig558g6442meb2f'
const congitoPoolId = 'us-east-1_QixjBWeYc'

const currentOrigin = window.location.origin;
const redirectSignIn = currentOrigin + '/';
const redirectSignOut = currentOrigin + '/';

Amplify.configure(
  {
    region: 'us-east-1',
    Auth: {
      Cognito: {
        userPoolId: congitoPoolId,
        userPoolClientId: cognitoClientId,
        allowGuestAccess: false,
        loginWith: {
          oauth: {
            domain: 'emotiv-dev.auth.us-east-1.amazoncognito.com',
            scopes: ['email', 'openid', 'profile', 'aws.cognito.signin.user.admin'],
            redirectSignIn: [redirectSignIn],
            redirectSignOut: [redirectSignOut],
            responseType: 'code',
          }
        }
      }
    }
  }
);

function APIDoc() {
  return (
    <RedocStandalone
      specUrl="/openapi.json"
      options={{
        nativeScrollbars: false,
        menuToggle: true,
        hideDownloadButton: true,
        hideHostname: false,
        sideNavStyle: SideNavStyleEnum.SummaryOnly,
      }}
    />
  );
}


export default function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    handleAuthCallback();
  }, []);

  async function handleAuthCallback() {
    try {
      // Check for OAuth errors in URL
      const urlParams = new URLSearchParams(window.location.search);
      const error = urlParams.get('error');
      const errorDescription = urlParams.get('error_description');
      var currentUser = null;

      if (error) {
        console.error('OAuth error:', error, errorDescription);
        setAuthError({
          error,
          description: errorDescription ? decodeURIComponent(errorDescription.replace(/\+/g, ' ')) : error
        });
        // Clean the URL
        window.history.replaceState({}, document.title, window.location.pathname);
        setLoading(false);
        return;
      }

      // Check if we're returning from OAuth redirect (code in URL)
      if (urlParams.has('code')) {
        console.log('Processing OAuth callback...');
        // Clean the URL
        window.history.replaceState({}, document.title, window.location.pathname);
      }

      try {
        currentUser = await getCurrentUser();
      } catch (err) {
        if (String(err).includes('UserUnAuthenticatedException')) {
          // on first load, user is not logged in -> no action needed
          return
        }
        console.error(err);
        throw err;
      }
      console.log(currentUser);
      if (!currentUser) {
        return
      }

      // Check for authenticated user
      if (currentUser && !currentUser.email) {
        setMsg('Fetching user attributes...');
        try {
          const attrs = await fetchUserAttributes();
          currentUser.email = attrs.email;
        } catch (err) {
          setAuthError({ error: String(err), description: 'Failed to fetch user attributes.' });
          setUser(null);
          return
        } finally {
          setMsg('');
        }
      }
      if (!currentUser.email.endsWith('@emotiv.com')) {
        setAuthError({ error: 'Unauthorized', description: 'You are not authorized to view this page.' });
        setUser(null);
        return;
      }
      setUser(currentUser);
    } catch (err) {
      setAuthError({ error: String(err), description: 'Authentication failed. Please try signing in again.' });
      setUser(null);
    } finally {
      setLoading(false);
    }
  }

  async function handleSignIn() {
    await signInWithRedirect();
  }

  async function handleSignOut() {
    await signOut();
    setUser(null);
  }

  async function signOutThenSignIn() {
    if (user) {
      await signOut();
    };
    await handleSignIn();
  }

  if (loading || msg || authError) {
    return (
      <View>
        {msg ? <Message variation='info' >{msg}</Message> : null}
        {loading ? <Loader variation='linear'/> : null}
        {
          authError? (
            <View>
              <Alert variation='error' heading={authError.error}>
                {authError.description}
              </Alert>
              <Button onClick={signOutThenSignIn} style={{marginTop: '20px'}}>Try Again</Button>
            </View>
          ) : null
        }
      </View>
    )
  }

  if (!user) {
    return (
      <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column'}}>
        <h2>Cerebrum API Documentation</h2>
        <Button onClick={handleSignIn}>Sign In with Cognito</Button>
      </div>
    );
  }

  return (
    <div>
      <Message variation='outlined' colorTheme='success' >
        You are logged in as {user.email} | <Link onClick={handleSignOut} href="#" display={"contents"}>Sign Out</Link>
      </Message>
      <APIDoc />
    </div>
  )
}

// Clear the existing HTML content
document.body.innerHTML = '<div class="app"></div>';
// Render your React component instead
const root = createRoot(document.getElementsByClassName('app')[0]);
root.render(<App />)
